import React, { useState } from 'react';
import { useSetAtom } from 'jotai';
import { Typography, Box, Paper, Button } from '@mui/material';

import { pageAtom } from '../../atoms';
import { PAGE } from '../../constant/page';
import {
  PasswordForm,
  TextInputForm,
  CheckBoxList,
} from '../../sharedComponents';
import { RegistrationButton } from './components';
import { INDUSTRY, REGEX } from '../../constant';

const boxStyles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginTop: '80px',
};

const paperStyles = {
  width: '50%',
  maxWidth: '700px',
  paddingX: '5%',
  paddingY: '50px',
};

type FormData = {
  email: string;
  password: string;
  lastName: string;
  firstName: string;
  company: string;
  phone: string;
  country: string;
  state: string;
  address: string;
  industry: string[];
  language: string;
};

const RegistrationPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    lastName: '',
    firstName: '',
    company: '',
    phone: '',
    country: 'JP',
    state: '',
    address: '',
    industry: [],
    language: 'Japanese',
  });

  const [formValidation, setFormValidation] = useState({
    email: true,
    password: true,
    lastName: true,
    firstName: true,
    company: true,
    phone: true,
    country: true,
    state: true,
    address: true,
    industry: true,
    language: true,
  });

  const updateFormData = <T extends keyof FormData>(
    key: T,
    value: FormData[T]
  ) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const updateValidity = (key: string, value: boolean) => {
    setFormValidation((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const setPage = useSetAtom(pageAtom);

  return (
    <Box {...boxStyles}>
      <Paper elevation={3} sx={paperStyles}>
        <Box
          display='flex'
          flexDirection='column'
          alignItems='center'
          gap='30px'
        >
          <Typography variant='h4'>{'アカウントの作成'}</Typography>
          <Box display='flex' flexDirection='row' width='100%' columnGap={6}>
            <TextInputForm
              label='姓'
              required
              value={formData.lastName}
              setValue={(value) => updateFormData('lastName', value)}
              isValid={formValidation.lastName}
              setIsValid={(value) => updateValidity('lastName', value)}
              validate={(input) => RegExp(REGEX.NAME).exec(input) !== null}
              errorMessage='正しい形式で入力してください'
              inputProps={{
                maxLength: 35,
              }}
            />
            <TextInputForm
              label='名'
              required
              value={formData.firstName}
              setValue={(value) => updateFormData('firstName', value)}
              isValid={formValidation.firstName}
              setIsValid={(value) => updateValidity('firstName', value)}
              validate={(input) => RegExp(REGEX.NAME).exec(input) !== null}
              errorMessage='正しい形式で入力してください'
              inputProps={{
                maxLength: 35,
              }}
            />
          </Box>
          <TextInputForm
            label='メールアドレス'
            required
            type='email'
            value={formData.email}
            setValue={(value) => updateFormData('email', value)}
            isValid={formValidation.email}
            setIsValid={(value) => updateValidity('email', value)}
            validate={(input) => RegExp(REGEX.EMAIL).exec(input) !== null}
            errorMessage='正しいメールアドレスの形式で入力してください'
          />

          <PasswordForm
            label='パスワード'
            required
            value={formData.password}
            setValue={(value) => updateFormData('password', value)}
            isValid={formValidation.password}
            setIsValid={(value) => updateValidity('password', value)}
            validate={(input) =>
              RegExp(REGEX.PASSWORD).exec(input) !== null &&
              !/(.)\1/.exec(input) &&
              new Set(input.split('')).size >= 5
            }
            errorMessage={
              <ul>
                <li>8-255文字</li>
                <li>最低5つのユニーク文字</li>
                <li>2つ以上連続した文字を含まない</li>
                <li>
                  最低1つ特殊文字(~!@#$%^&*()-_=+[]{}|;:,.{'<>'}/?)を含む
                </li>
                <li>最低1つ数字を含む</li>
                <li>最低1つ大文字 (A-Z)を含む</li>
                <li>最低1つ小文字(a-z)を含む</li>
              </ul>
            }
            inputProps={{
              maxLength: 255,
            }}
          />
          <Box />

          <TextInputForm
            label='会社名'
            required
            value={formData.company}
            setValue={(value) => updateFormData('company', value)}
            isValid={formValidation.company}
            setIsValid={(value) => updateValidity('company', value)}
            validate={(input) => RegExp(REGEX.COMPANY).exec(input) !== null}
            errorMessage='正しい形式で入力してください'
            inputProps={{
              maxLength: 80,
            }}
          />

          <TextInputForm
            label='電話番号'
            value={formData.phone}
            setValue={(value) => updateFormData('phone', value)}
            isValid={formValidation.phone}
            setIsValid={(value) => updateValidity('phone', value)}
            validate={(input) => RegExp(REGEX.PHONE).exec(input) !== null}
            errorMessage='正しい形式で入力してください'
            inputProps={{
              maxLength: 32,
            }}
          />

          <Box display='flex' flexDirection='row' width='100%' columnGap={6}>
            <TextInputForm
              label='国'
              disabled
              required
              value={'日本'}
              setValue={() => updateFormData('country', 'JP')}
              isValid={formValidation.country}
              setIsValid={(value) => updateValidity('country', value)}
              validate={() => true}
              errorMessage='正しい形式で入力してください'
            />

            <TextInputForm
              label='州'
              disabled
              value={formData.state}
              setValue={(value) => updateFormData('state', value)}
              isValid={formValidation.state}
              setIsValid={(value) => updateValidity('state', value)}
              validate={() => true}
              errorMessage='正しい形式で入力してください'
            />
          </Box>

          <TextInputForm
            label='住所'
            value={formData.address}
            setValue={(value) => updateFormData('address', value)}
            isValid={formValidation.address}
            setIsValid={(value) => updateValidity('address', value)}
            validate={(input) => RegExp(REGEX.ADDRESS).exec(input) !== null}
            errorMessage='正しい形式で入力してください'
            inputProps={{
              maxLength: 40,
            }}
          />

          <Box
            display='flex'
            flexDirection='column'
            width='100%'
            justifyContent='center'
          >
            <Typography variant='body1' fontWeight='bold'>
              {'職種'}
            </Typography>

            <CheckBoxList
              items={INDUSTRY}
              selected={formData.industry}
              setSelected={(values) => updateFormData('industry', values)}
            />
          </Box>
          <Box
            display='flex'
            flexDirection='row'
            width='100%'
            justifyContent='center'
            columnGap={24}
          >
            <Button
              variant='contained'
              sx={{ width: '150px' }}
              color='inherit'
              onClick={() => {
                setPage(PAGE.TERMS_OF_USE);
              }}
            >
              戻る
            </Button>
            <RegistrationButton
              formData={formData}
              formValidation={formValidation}
              setFormValidation={setFormValidation}
            />
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default React.memo(RegistrationPage);
